import * as React from "react";
import {
  Dialog as AriaDialog,
  DialogTrigger as AriaDialogTrigger,
  Modal,
  ModalOverlay,
  Heading,
  type DialogProps as AriaDialogProps,
  type DialogTriggerProps as AriaDialogTriggerProps,
  type ModalOverlayProps,
} from "react-aria-components";
import { XIcon } from "lucide-react";

import { cn } from "./utils";
import { Button } from "./button";
import { useDesignSystemText } from "../i18n";

export interface DialogProps extends Omit<AriaDialogTriggerProps, "className"> {}

function Dialog({ children, ...props }: DialogProps) {
  return (
    <AriaDialogTrigger data-slot="dialog" {...props}>
      {children}
    </AriaDialogTrigger>
  );
}

function DialogTrigger({ children, ...props }: React.ComponentProps<typeof Button>) {
  return (
    <Button data-slot="dialog-trigger" {...props}>
      {children}
    </Button>
  );
}

export interface DialogContentProps extends Omit<ModalOverlayProps, "className"> {
  className?: string;
  children?: React.ReactNode;
}

function DialogContent({ className, children, ...props }: DialogContentProps) {
  const text = useDesignSystemText();

  return (
    <ModalOverlay
      data-slot="dialog-overlay"
      className={cn(
        "entering:animate-in exiting:animate-out exiting:fade-out-0 entering:fade-in-0 fixed inset-0 z-50 bg-black/50",
      )}
      {...props}
    >
      <Modal
        data-slot="dialog-content"
        className={cn(
          "bg-background entering:animate-in exiting:animate-out exiting:fade-out-0 entering:fade-in-0 exiting:zoom-out-95 entering:zoom-in-95 fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border p-6 shadow-lg duration-200 sm:max-w-lg",
          className,
        )}
      >
        <AriaDialog className="outline-none min-h-0 h-full">
          {({ close }) => (
            <>
              {children}
              <Button
                onPress={close}
                variant="ghost"
                size="icon"
                className="absolute top-4 right-4 size-auto p-1 opacity-70 hovered:opacity-100"
              >
                <XIcon className="size-4" />
                <span className="sr-only">{text("dialog.close")}</span>
              </Button>
            </>
          )}
        </AriaDialog>
      </Modal>
    </ModalOverlay>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn("flex flex-col gap-2 text-center sm:text-left", className)}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        className,
      )}
      {...props}
    />
  );
}

function DialogTitle({ className, ...props }: React.ComponentProps<typeof Heading>) {
  return (
    <Heading
      slot="title"
      data-slot="dialog-title"
      className={cn("text-lg leading-none font-semibold", className)}
      {...props}
    />
  );
}

function DialogDescription({ className, ...props }: React.ComponentProps<"p">) {
  return (
    <p
      data-slot="dialog-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
}

function DialogClose({ children, ...props }: React.ComponentProps<typeof Button>) {
  return (
    <Button data-slot="dialog-close" {...props}>
      {children}
    </Button>
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
};
